// #127 Sevkiyat Aracı "Kriter" sekmesi. Müşteri grubu / ek (alt) grup / özelleştirilmiş
// grup / özelleştirilmiş alan / satış temsilcisi kriterleri (hepsi VE ile bağlanır) ve
// harita üzerinde "kalemle" çizilen serbest "Sevkiyat Alanı". Seçilen kriterlere uyan
// müşteriler haritada vurgulanır ve canlı sayı gösterilir. Kriterler sonraki aşamada
// sipariş planlama ekranında kullanılacak.
import { useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders } from '../listPageStyles';
import { RELATION_CONFIGS } from '../relationConfigs';
import { resolveLabels } from '../campaign/campaignUtils';
import { Section, Grid, FieldCustom } from '../detail/DetailShell';
import { MultiChipSelect } from '../campaign/MultiChipSelect';
import ShipmentAreaMap, { pointInAnyPolygon } from './ShipmentAreaMap';

const unwrap = (p) => (p && (p.Data ?? p.data)) ?? p;

// Id dizisini chip {value,label} dizisine çözer; kullanıcı değişikliği parent'a yansır.
function useChipItems(ids, endpoint, setIds) {
  const [items, setItems] = useState([]);
  const loadedRef = useRef(null);
  useEffect(() => {
    const sig = (ids || []).join(',');
    if (sig === loadedRef.current) return; // kendi güncellememiz — tekrar çözme
    loadedRef.current = sig;
    if (!ids || !ids.length) { setItems([]); return undefined; }
    let alive = true;
    resolveLabels(endpoint, ids)
      .then((map) => { if (alive) setItems(ids.map((id) => ({ value: id, label: map[id] || id }))); })
      .catch(() => { if (alive) setItems(ids.map((id) => ({ value: id, label: id }))); });
    return () => { alive = false; };
  }, [ids, endpoint]);
  const onChange = (next) => {
    loadedRef.current = next.map((x) => x.value).join(',');
    setItems(next);
    setIds(next.map((x) => x.value));
  };
  return [items, onChange];
}

export default function ShipmentCriteriaTab({ criteria, setCriteria }) {
  const c = criteria || {};
  const patch = (k, v) => setCriteria((prev) => ({ ...prev, [k]: v }));

  const [grpItems, setGrpItems] = useChipItems(c.customerGroupIds, '/api/customergroups', (ids) => patch('customerGroupIds', ids));
  const [subItems, setSubItems] = useChipItems(c.subGroupIds, '/api/customersubgroups', (ids) => patch('subGroupIds', ids));
  const [repItems, setRepItems] = useChipItems(c.salesRepIds, '/api/representatives', (ids) => patch('salesRepIds', ids));

  // #770.3 "Özelleştirilmiş" satırının TAMAMI bu ekrandan kaldırıldı.
  //
  // İlk turda yalnız "Özelleştirilmiş ALAN" (alan + değer) çifti kaldırılmış, "Özelleştirilmiş GRUP"
  // bırakılmıştı — talep ise satırın ekranda hiç olmamasıydı ("bu ekranda bu satırın olmaması lazım").
  // Araç kriteri müşteri kümesini grup / alt grup / temsilci ile daraltır; özelleştirilmiş tanımlar
  // müşteri kartının işidir. Eski kayıtların criteriaJson'ında dynamicGroupIds/additionalFields
  // kalmış olabilir; okunmaz ve yazılmaz.

  // Harita tabanı — kullanıcının distribütör kapsamındaki müşteriler.
  const [baseCustomers, setBaseCustomers] = useState([]);
  const [baseErr, setBaseErr] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch('/api/customers/scoped-points?take=3000', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (!alive) return;
        const rows = (unwrap(d) || []).map((x) => ({
          id: x.id, code: x.textId, label: x.title,
          lat: Number(x.latitude), lng: Number(x.longitude),
          neighborhood: x.neighborhood, street: x.street,
        }));
        setBaseCustomers(rows);
      })
      .catch(() => { if (alive) setBaseErr(true); });
    return () => { alive = false; };
  }, []);

  // Kriter önizlemesi (alan hariç) — backend'de VE mantığıyla eşleşen müşteri Id'leri.
  const [matchedIds, setMatchedIds] = useState(null); // null = kriter yok
  // #770.3 additionalFields ölçütten ÇIKARILDI: alan artık bu ekranda YOK, dolayısıyla önizlemeyi de
  // etkilememeli. Eski kayıtların criteriaJson'ında kalmış değerler sessizce filtre uygulamaya devam
  // ederse kullanıcı ekranda görmediği bir kriterle daralan bir sonuç görürdü.
  const hasNonSpatialCriteria = (c.customerGroupIds?.length || c.subGroupIds?.length
    || c.dynamicGroupIds?.length || c.salesRepIds?.length) > 0;

  const previewKey = JSON.stringify({
    g: c.customerGroupIds || [], s: c.subGroupIds || [], d: c.dynamicGroupIds || [],
    r: c.salesRepIds || [],
  });
  useEffect(() => {
    if (!hasNonSpatialCriteria) { setMatchedIds(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      fetch('/api/shipmentvehicles/criteria-preview', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          customerGroupIds: c.customerGroupIds || [],
          subGroupIds: c.subGroupIds || [],
          dynamicGroupIds: c.dynamicGroupIds || [],
          salesRepIds: c.salesRepIds || [],
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((d) => { if (alive) setMatchedIds(new Set((unwrap(d)?.customers || []).map((x) => x.id))); })
        .catch(() => { if (alive) setMatchedIds(new Set()); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, hasNonSpatialCriteria]);

  const polygons = useMemo(() => c.shipmentArea?.polygons || [], [c.shipmentArea]);
  const setPolygons = (next) => patch('shipmentArea', { ...(c.shipmentArea || {}), polygons: next });

  // Nihai seçili sayısı (kriter ∩ alan) — başlık özetinde göster.
  const selectedCount = useMemo(() => {
    const hasArea = polygons.length > 0;
    let n = 0;
    for (const cust of baseCustomers) {
      if (!Number.isFinite(cust.lat) || !Number.isFinite(cust.lng)) continue;
      const matched = !matchedIds || matchedIds.has(cust.id);
      const inArea = hasArea ? pointInAnyPolygon(cust.lat, cust.lng, polygons) : true;
      if (matched && inArea) n++;
    }
    return n;
  }, [baseCustomers, matchedIds, polygons]);

  return (
    <div className="dps-form">
      <Section icon="users" title="Müşteri Kriterleri"
        subtitle="Tüm kriterler VE ile bağlanır. Bir kriter alanı boşsa o kriter uygulanmaz."
        right={<span style={{ fontSize: 13, color: 'var(--text-secondary,#64748b)' }}>Seçili müşteri: <b style={{ color: '#16a34a' }}>{selectedCount}</b></span>}
      >
        <Grid cols={2}>
          <FieldCustom label="Müşteri Grubu">
            <MultiChipSelect items={grpItems} onChange={setGrpItems} config={RELATION_CONFIGS.customerGroup} placeholder="Müşteri grubu ara ve ekle…" />
          </FieldCustom>
          <FieldCustom label="Ek (Alt) Grup">
            <MultiChipSelect items={subItems} onChange={setSubItems} config={RELATION_CONFIGS.customerSubGroup} placeholder="Alt grup ara ve ekle…" />
          </FieldCustom>
          <FieldCustom label="Satış Temsilcisi">
            <MultiChipSelect items={repItems} onChange={setRepItems} config={RELATION_CONFIGS.representative} placeholder="Satış temsilcisi ara ve ekle…" />
          </FieldCustom>
        </Grid>

      </Section>

      <Section icon="mapPin" title="Sevkiyat Alanı"
        subtitle="Haritada fareyle basılı tutup “kalemle çizer gibi” serbest alan(lar) çizin. Mahalle/sokak detayında yakınlaştırabilirsiniz. Kriterlere uyan müşteriler yeşil, alan dışı kalanlar amber gösterilir."
      >
        {baseErr && (
          <div className="dps-note" style={{ marginBottom: 10 }}>
            Müşteri konumları yüklenemedi. Yine de alan çizip kaydedebilirsiniz.
          </div>
        )}
        <ShipmentAreaMap
          customers={baseCustomers}
          matchedIds={matchedIds}
          polygons={polygons}
          onPolygonsChange={setPolygons}
          height={480}
        />
      </Section>
    </div>
  );
}
